import React from 'react';
import { Battery, Cpu, Grid3x3, Lightbulb, Building2, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Products = () => {
  const manufacturingProducts = [
    {
      icon: <Cpu className="h-8 w-8" />,
      title: "High-Efficiency Photovoltaic (PV) Modules",
      description: "Advanced solar panels with superior conversion efficiency"
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Solar Cells & Inverters",
      description: "Cutting-edge solar cells and smart inverter systems"
    },
    {
      icon: <Grid3x3 className="h-8 w-8" />,
      title: "Module Mounting Structures (MMS)",
      description: "Durable mounting solutions for all installation types"
    },
    {
      icon: <Battery className="h-8 w-8" />,
      title: "Solar Batteries & Storage Solutions",
      description: "Energy storage systems for 24/7 power availability"
    },
    {
      icon: <Lightbulb className="h-8 w-8" />,
      title: "Balance of System (BoS) Components",
      description: "Complete system components for optimal performance"
    }
  ];

  const epcServices = [
    {
      icon: <Building2 className="h-8 w-8" />,
      title: "Utility-Scale Solar Power Plants",
      description: "Large-scale solar installations for maximum power generation"
    },
    {
      icon: <Building2 className="h-8 w-8" />,
      title: "Commercial & Industrial Rooftop Solar",
      description: "Customized rooftop solutions for businesses"
    },
    {
      icon: <Grid3x3 className="h-8 w-8" />,
      title: "Micro-Grids for Remote Locations",
      description: "Independent power systems for off-grid areas"
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Government Infrastructure Projects",
      description: "Turnkey solutions for public sector energy needs"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-[#1e3a8a] to-[#2563eb]">
        <div className="max-w-5xl mx-auto text-center text-white">
          <h1 className="text-6xl font-bold mb-6">Products & Services</h1>
          <p className="text-2xl text-gray-100">
            World-Class Solar Solutions & Infrastructure
          </p>
        </div>
      </section>

      {/* Manufacturing & Supply Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1e3a8a] mb-6">
              Manufacturing & Supply
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              World-Class Solar Components engineered for durability and maximum efficiency
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {manufacturingProducts.map((product, index) => (
              <div 
                key={index}
                className="bg-white border-2 border-gray-200 p-8 rounded-2xl hover:border-[#fbbf24] hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-[#fbbf24] rounded-xl flex items-center justify-center mb-6 text-[#1e3a8a]">
                  {product.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">
                  {product.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {product.description}
                </p>
              </div>
            ))}
          </div>

          {/* Product Images */}
          <div className="grid md:grid-cols-4 gap-6">
            <img 
              src="https://images.unsplash.com/photo-1604062604180-60878425cd1c?w=400&q=85" 
              alt="PV Modules" 
              className="rounded-xl shadow-lg h-64 w-full object-cover"
            />
            <img 
              src="https://images.unsplash.com/photo-1745162451436-2593789f3c94?w=400&q=85" 
              alt="Mounting Structures" 
              className="rounded-xl shadow-lg h-64 w-full object-cover"
            />
            <img 
              src="https://images.unsplash.com/photo-1768839727824-28d6f0dcd1d1?w=400&q=85" 
              alt="Solar Field" 
              className="rounded-xl shadow-lg h-64 w-full object-cover"
            />
            <img 
              src="https://images.unsplash.com/photo-1521618755572-156ae0cdd74d?w=400&q=85" 
              alt="Utility Plant" 
              className="rounded-xl shadow-lg h-64 w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* EPC & Infrastructure Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1e3a8a] mb-6">
              Engineering, Procurement & Construction
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              We provide turnkey solutions for complex energy needs. Our team handles everything 
              from soil testing and engineering design to final commissioning.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            {epcServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#fbbf24] rounded-xl flex items-center justify-center mb-6 text-[#1e3a8a]">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">
                  {service.title}
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-[#fbbf24]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#1e3a8a]/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold text-center text-[#1e3a8a] mb-16">
            Our EPC Process
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center relative">
              {/* Connector line */}
              <div className="hidden md:block absolute top-10 left-1/2 w-full h-1 bg-gradient-to-r from-[#fbbf24] to-[#1e3a8a]"></div>
              
              <div className="w-20 h-20 bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold relative z-10 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Site Assessment</h3>
              <p className="text-gray-700">Soil testing and feasibility analysis</p>
            </div>

            <div className="text-center relative">
              <div className="hidden md:block absolute top-10 left-1/2 w-full h-1 bg-gradient-to-r from-[#1e3a8a] to-[#fbbf24]"></div>
              
              <div className="w-20 h-20 bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold relative z-10 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Engineering Design</h3>
              <p className="text-gray-700">Custom solutions tailored to your needs</p>
            </div>

            <div className="text-center relative">
              <div className="hidden md:block absolute top-10 left-1/2 w-full h-1 bg-gradient-to-r from-[#fbbf24] to-[#1e3a8a]"></div>
              
              <div className="w-20 h-20 bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold relative z-10 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Construction</h3>
              <p className="text-gray-700">Professional installation with quality control</p>
            </div>

            <div className="text-center relative">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold relative z-10 shadow-lg">
                4
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Commissioning</h3>
              <p className="text-gray-700">Testing and final handover</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Products;
